/**
 * MinLOS Resolver — pure, dependency-free resolution of the effective
 * minimum length of stay for a single date.
 *
 * Used by THREE call sites that must stay in sync:
 *   1. minLosService.validateStayLength   (booking-time validation)
 *   2. webHotelierPricingBridge           (child-rate restriction push)
 *   3. supabase/functions/pricing-engine  (parent-rate push — mirrored port)
 *
 * Keep this file free of Supabase / network / Date.now() so it can be unit
 * tested and ported to the Deno edge function verbatim.
 */

import type {
  MinLosGapOverride,
  MinLosLastMinuteOverride,
} from '@/types/revenue';

/** Raw conditions JSON as stored in pricing_rules.conditions for min_los. */
export interface MinLosConditionsJson {
  min_los?: number;
  /** @deprecated legacy binary flag — normalized into gapOverride below. */
  allow_gap_fill_override?: boolean;
  gap_override?: {
    enabled?: boolean;
    gap_nights_min?: number;
    gap_nights_max?: number;
    override_min_los?: number;
    /** When true, override target equals the detected gap length. */
    match_gap_length?: boolean;
  };
  last_minute_override?: {
    enabled?: boolean;
    days_ahead_max?: number;
    override_min_los?: number;
  };
}

export interface NormalizedMinLosOverrides {
  gapOverride?: MinLosGapOverride;
  lastMinuteOverride?: MinLosLastMinuteOverride;
}

/**
 * Translate the stored conditions JSON into camelCase override configs.
 *
 * Backward compatibility: a legacy rule that only has
 * `allow_gap_fill_override: true` (and no structured `gap_override`) is treated
 * as "relax to 1 night for a gap of any length" — exactly the behavior the old
 * hardcoded path produced — so existing rules keep working unchanged.
 */
export function normalizeMinLosOverrides(
  conditions: MinLosConditionsJson | null | undefined
): NormalizedMinLosOverrides {
  if (!conditions) return {};

  const result: NormalizedMinLosOverrides = {};

  if (conditions.gap_override) {
    const g = conditions.gap_override;
    result.gapOverride = {
      enabled: g.enabled ?? false,
      gapNightsMin: g.gap_nights_min ?? 1,
      gapNightsMax: g.gap_nights_max ?? Number.MAX_SAFE_INTEGER,
      overrideMinLos: g.override_min_los ?? 1,
      matchGapLength: g.match_gap_length === true,
    };
  } else if (conditions.allow_gap_fill_override === true) {
    // Legacy: any gap relaxes minLos to 1.
    result.gapOverride = {
      enabled: true,
      gapNightsMin: 1,
      gapNightsMax: Number.MAX_SAFE_INTEGER,
      overrideMinLos: 1,
    };
  }

  if (conditions.last_minute_override) {
    const lm = conditions.last_minute_override;
    result.lastMinuteOverride = {
      enabled: lm.enabled ?? false,
      daysAheadMax: lm.days_ahead_max ?? 0,
      overrideMinLos: lm.override_min_los ?? 1,
    };
  }

  return result;
}

/**
 * Does a gap of `gapNights` nights qualify for the configured gap override?
 * Returns false when the date is not in a gap (gapNights undefined), the
 * override is disabled, or the gap length is outside [min, max] (inclusive).
 */
export function gapQualifies(
  gapNights: number | undefined,
  gapOverride: MinLosGapOverride | undefined
): boolean {
  if (gapNights == null || !gapOverride?.enabled) return false;
  return (
    gapNights >= gapOverride.gapNightsMin &&
    gapNights <= gapOverride.gapNightsMax
  );
}

/**
 * Is the arrival date within the configured last-minute window?
 * `daysAhead` is the number of days from "today" to the arrival date
 * (0 = arriving today). Negative / undefined => not last-minute.
 */
export function lastMinuteQualifies(
  daysAhead: number | undefined,
  lastMinuteOverride: MinLosLastMinuteOverride | undefined
): boolean {
  if (daysAhead == null || daysAhead < 0 || !lastMinuteOverride?.enabled) {
    return false;
  }
  return daysAhead <= lastMinuteOverride.daysAheadMax;
}

export interface MinLosResolveInput {
  /** minLos from the applicable min_los rule before any override. */
  baseMinLos: number;
  gapOverride?: MinLosGapOverride;
  /** Length (nights) of the gap this date sits in; undefined if not in a gap. */
  gapNights?: number;
  lastMinuteOverride?: MinLosLastMinuteOverride;
  /** Days from today to the arrival date; undefined if not applicable. */
  daysAhead?: number;
}

export interface MinLosResolveResult {
  effectiveMinLos: number;
  appliedOverride: 'none' | 'gap' | 'last_minute';
  /** Every value that was considered — useful for log lines and debugging. */
  candidates: number[];
}

/**
 * Resolve the effective minLOS for one date.
 *
 * ── BUSINESS DECISION (your contribution) ──────────────────────────────────
 * This is the heart of the feature. The owner described two independent ways
 * the strict minLOS (e.g. 5) should be relaxed:
 *   • Orphan gap : date is inside a 2–4 night gap  → minLOS becomes e.g. 2
 *   • Last minute: arrival is ≤ 2 days away         → minLOS becomes e.g. 2
 *
 * You decided (Q2) that when BOTH apply on the same date, the LOWEST value
 * wins — maximize bookability. Also: an override must never RAISE the minimum
 * above `baseMinLos` (overrides only ever relax).
 *
 * Implement the ~6–10 lines below:
 *   1. Start the candidate list with `baseMinLos`.
 *   2. If `gapQualifies(input.gapNights, input.gapOverride)` push
 *      `input.gapOverride.overrideMinLos`.
 *   3. If `lastMinuteQualifies(input.daysAhead, input.lastMinuteOverride)`
 *      push `input.lastMinuteOverride.overrideMinLos`.
 *   4. effectiveMinLos = min(...candidates), clamped to at least 1.
 *   5. Set `appliedOverride` to which one produced the winning value
 *      ('none' if base won; pick a sensible tie-break if both override
 *      values are equal — gap vs last_minute is your call, document it).
 *
 * Edge cases worth a thought: equal override values (tie-break), an override
 * value accidentally larger than base (your min() already protects this),
 * gapNights/daysAhead undefined (the *Qualifies helpers already guard).
 * ───────────────────────────────────────────────────────────────────────────
 */
export function resolveEffectiveMinLos(
  input: MinLosResolveInput
): MinLosResolveResult {
  const base = input.baseMinLos;
  const candidates: number[] = [base];

  const gapApplies = gapQualifies(input.gapNights, input.gapOverride);
  const lmApplies = lastMinuteQualifies(
    input.daysAhead,
    input.lastMinuteOverride
  );

  // Start from the base and only ever move DOWN (never raise). We keep the
  // lowest value (most permissive — maximize bookability). Gap is evaluated
  // before last-minute and last-minute replaces only on a STRICT decrease, so
  // when both produce the same value the GAP override wins the tie.
  let best = base;
  let appliedOverride: MinLosResolveResult['appliedOverride'] = 'none';

  if (gapApplies && input.gapOverride) {
    // Two modes:
    //   • matchGapLength: gapVal = the actual gap length — prevents partial
    //     fills that would re-orphan part of the same gap.
    //   • fixed: gapVal = the rule's configured overrideMinLos.
    // `gapApplies` already guarantees input.gapNights is defined and in range.
    // If gapNights > baseMinLos the trailing `if (best >= base)` clamp makes
    // this a no-op, so no extra guard is needed here.
    const gapVal =
      input.gapOverride.matchGapLength && input.gapNights != null
        ? input.gapNights
        : input.gapOverride.overrideMinLos;
    candidates.push(gapVal);
    if (gapVal < best) {
      best = gapVal;
      appliedOverride = 'gap';
    }
  }

  if (lmApplies && input.lastMinuteOverride) {
    const lmVal = input.lastMinuteOverride.overrideMinLos;
    candidates.push(lmVal);
    if (lmVal < best) {
      best = lmVal;
      appliedOverride = 'last_minute';
    }
  }

  // Clamp: minimum length of stay can never be below 1 night even if an
  // override is misconfigured to 0/negative. If nothing actually lowered the
  // base (override value >= base), report 'none'.
  const effectiveMinLos = Math.max(1, best);
  if (best >= base) {
    appliedOverride = 'none';
  }

  return { effectiveMinLos, appliedOverride, candidates };
}
